import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

/**
 * Create Stripe Connect account for professional
 * @returns {Promise} Account creation response
 */
export const createConnectAccount = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(
      getApiUrl('/stripe-connect/create'),
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error creating Connect account:', error);
    throw error;
  }
};

/**
 * Get Stripe Connect onboarding link
 * @returns {Promise} Onboarding link URL
 */
export const getOnboardingLink = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(getApiUrl('/stripe-connect/onboarding-link'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error getting onboarding link:', error);
    throw error;
  }
};

/**
 * Get Stripe Connect account status
 * @returns {Promise} Account status information
 */
export const getAccountStatus = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(getApiUrl('/stripe-connect/status'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching account status:', error);
    throw error;
  }
};

/**
 * Refresh onboarding link (if expired)
 * @returns {Promise} New onboarding link
 */
export const refreshOnboardingLink = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(
      getApiUrl('/stripe-connect/refresh-onboarding'),
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error refreshing onboarding link:', error);
    throw error;
  }
};

/**
 * Disconnect Stripe Connect account
 * @returns {Promise} Disconnection confirmation
 */
export const disconnectAccount = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.delete(getApiUrl('/stripe-connect/disconnect'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error disconnecting account:', error);
    throw error;
  }
};

/**
 * Handle return from Stripe Connect onboarding
 * @returns {Promise} Updated account status
 */
export const handleConnectReturn = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(getApiUrl('/stripe-connect/return'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error handling Connect return:', error);
    throw error;
  }
};

/**
 * Get user-friendly status message
 * @param {string} status - Status code from API
 * @returns {Object} Status message and color
 */
export const getStatusMessage = (status) => {
  const statusMessages = {
    'not_connected': {
      message: 'Bank account not connected',
      color: 'gray',
      action: 'Connect your bank account to receive payouts'
    },
    'pending': {
      message: 'Verification pending',
      color: 'yellow',
      action: 'Complete verification in Stripe'
    },
    'requires_information': {
      message: 'Additional information required',
      color: 'orange',
      action: 'Update your account information'
    },
    'verification_pending': {
      message: 'Verification in progress',
      color: 'blue',
      action: 'Stripe is verifying your account'
    },
    'active': {
      message: 'Account active',
      color: 'green',
      action: 'Ready to receive payouts'
    }
  };

  return statusMessages[status] || statusMessages['not_connected'];
};
